import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Shield, Lock, Eye, FileText } from 'lucide-react'

export default function Datenschutz() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Datenschutz"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0a0a0a] to-[#0a0a0a]" />
        </div>
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[120px] z-0" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Shield className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Ihre Daten</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1]"
          >
            Datenschutz<span className="italic text-white/70">erklärung</span>
          </motion.h1>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTENT
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 relative z-10 space-y-12">
          
          {/* 1. Datenschutz auf einen Blick */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-2xl text-white">1. Datenschutz auf einen Blick</h2>
            </div>

            <div className="space-y-6 text-white/50">
              <div>
                <h3 className="text-white font-medium mb-2">Allgemeine Hinweise</h3>
                <p className="leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen.
                </p>
              </div>

              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl space-y-4">
                <h3 className="text-white font-medium">Datenerfassung auf dieser Website</h3>
                
                <div>
                  <h4 className="text-white text-sm font-medium mb-1">Wer ist verantwortlich?</h4>
                  <p className="text-sm leading-relaxed">
                    Die Datenverarbeitung erfolgt durch den Websitebetreiber. Kontaktdaten finden Sie im Abschnitt „Verantwortliche Stelle".
                  </p>
                </div>

                <div>
                  <h4 className="text-white text-sm font-medium mb-1">Wie erfassen wir Ihre Daten?</h4>
                  <p className="text-sm leading-relaxed">
                    Ihre Daten werden durch Ihre Mitteilungen sowie automatisch durch IT-Systeme beim Besuch erfasst.
                  </p>
                </div>

                <div>
                  <h4 className="text-white text-sm font-medium mb-1">Welche Rechte haben Sie?</h4>
                  <p className="text-sm leading-relaxed">
                    Sie haben das Recht auf Auskunft, Berichtigung oder Löschung Ihrer gespeicherten Daten.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 2. Hosting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-2xl text-white">2. Hosting</h2>
            </div>

            <div className="text-white/50 space-y-4">
              <p className="leading-relaxed">Wir hosten die Inhalte unserer Website bei folgendem Anbieter:</p>
              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl space-y-4">
                <h3 className="text-white font-medium mb-2">All-Inkl</h3>
                <p className="text-sm leading-relaxed">
                  Anbieter ist die ALL-INKL.COM - Neue Medien Münnich, Inh. René Münnich, Hauptstraße 68, 02742 Friedersdorf (nachfolgend All-Inkl). Details entnehmen Sie der Datenschutzerklärung von All-Inkl:{' '}
                  <a href="https://all-inkl.com/datenschutzinformationen/" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 transition-colors">
                    https://all-inkl.com/datenschutzinformationen/
                  </a>
                  .
                </p>
                <p className="text-sm leading-relaxed">
                  Die Verwendung von All-Inkl erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung unserer Website. Sofern eine entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TDDDG, soweit die Einwilligung die Speicherung von Cookies oder den Zugriff auf Informationen im Endgerät des Nutzers (z. B. Device-Fingerprinting) im Sinne des TDDDG umfasst. Die Einwilligung ist jederzeit widerrufbar.
                </p>
                <p className="text-sm leading-relaxed font-medium text-white/70">
                  Auftragsverarbeitung
                </p>
                <p className="text-sm leading-relaxed">
                  Wir haben einen Vertrag über Auftragsverarbeitung (AVV) zur Nutzung des oben genannten Dienstes geschlossen. Hierbei handelt es sich um einen datenschutzrechtlich vorgeschriebenen Vertrag, der gewährleistet, dass dieser die personenbezogenen Daten unserer Websitebesucher nur nach unseren Weisungen und unter Einhaltung der DSGVO verarbeitet.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 3. Allgemeine Hinweise */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-2xl text-white">3. Allgemeine Hinweise</h2>
            </div>

            <div className="text-white/50 space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Hinweis zur verantwortlichen Stelle</h3>
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
                  <p className="text-white font-medium mb-2">Reese Restaurant GmbH</p>
                  <p className="text-sm">Hückelsmaystr. 70<br />47804 Krefeld</p>
                  <p className="text-sm mt-2">
                    <span className="text-white/70">Telefon:</span>{' '}
                    <a href="tel:+4915755537615" className="text-brand-400 hover:text-brand-300 transition-colors">01575 5537615</a><br />
                    <span className="text-white/70">E-Mail:</span>{' '}
                    <a href="mailto:info@hueckels-may.de" className="text-brand-400 hover:text-brand-300 transition-colors">
                      info@hueckels-may.de
                    </a>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Speicherdauer</h3>
                <p className="leading-relaxed text-sm">
                  Ihre personenbezogenen Daten verbleiben bei uns, bis der Zweck für die Datenverarbeitung entfällt oder gesetzliche Aufbewahrungsfristen ablaufen.
                </p>
              </div>

              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
                <h3 className="text-white font-medium mb-2">Widerspruchsrecht (Art. 21 DSGVO)</h3>
                <p className="text-sm leading-relaxed">
                  Sie haben jederzeit das Recht, gegen die Verarbeitung Ihrer Daten Widerspruch einzulegen. Kontaktieren Sie uns hierzu unter den oben genannten Kontaktdaten.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 4. Datenerfassung */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-2xl text-white">4. Datenerfassung</h2>
            </div>

            <div className="text-white/50 space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Cookies</h3>
                <p className="text-sm leading-relaxed">
                  Unsere Website verwendet Cookies. Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden.
                </p>
              </div>

              <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
                <h3 className="text-white font-medium mb-2">Kontaktformular</h3>
                <p className="text-sm leading-relaxed">
                  Wenn Sie uns per Kontaktformular Anfragen senden, werden Ihre Angaben zur Bearbeitung der Anfrage gespeichert. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 5. Plugins und Tools */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-2xl text-white">5. Plugins und Tools</h2>
            </div>

            <div className="text-white/50 space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Google Maps</h3>
                <p className="text-sm leading-relaxed mb-2">
                  Diese Seite nutzt den Kartendienst Google Maps. Anbieter ist die Google Ireland Limited.
                </p>
                <a href="https://policies.google.com/privacy?hl=de" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 text-sm transition-colors">
                  Mehr Informationen →
                </a>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Google reCAPTCHA</h3>
                <p className="text-sm leading-relaxed">
                  Wir nutzen Google reCAPTCHA zum Schutz vor automatisierten Zugriffen.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Quelle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center pt-8 border-t border-white/5"
          >
            <p className="text-sm text-white/30">
              Quelle:{' '}
              <a href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer" className="text-brand-400/60 hover:text-brand-400 transition-colors">
                https://www.e-recht24.de
              </a>
            </p>
          </motion.div>

        </div>
      </section>

    </div>
  )
}
